import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


// Import the comprehensive design components from appearance customization
import WidgetSizeSelector from '../../appearance-customization/components/WidgetSizeSelector';
import ChatBubbleCustomizer from '../../appearance-customization/components/ChatBubbleCustomizer';
import AvatarUploader from '../../appearance-customization/components/AvatarUploader';
import TypographyControls from '../../appearance-customization/components/TypographyControls';
import ColorSchemeCustomizer from '../../appearance-customization/components/ColorSchemeCustomizer';
import TaglineConfigurator from '../../appearance-customization/components/TaglineConfigurator';
import StatusMessagesConfig from '../../appearance-customization/components/StatusMessagesConfig';
import TypingSimulationControls from '../../appearance-customization/components/TypingSimulationControls';
import LivePreviewPanel from '../../appearance-customization/components/LivePreviewPanel';

const StyleSettings = ({ config, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('widget');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Extract appearance from config or set comprehensive defaults
  const appearance = config?.appearance || {
    theme: 'modern',
    colorScheme: {
      primary: '#21b17a',
      primaryHover: '#1a8f65',
      accent: '#3b82f6',
      chatBackground: '#ffffff',
      messageBackground: '#f3f4f6',
      userMessageBackground: '#21b17a',
      textPrimary: '#1f2937',
      textSecondary: '#6b7280',
      textMuted: '#9ca3af',
      border: '#e5e7eb',
      borderLight: '#f3f4f6',
      divider: '#e5e7eb'
    },
    typography: {
      fontFamily: 'system',
      fontSize: '14'
    },
    widget: {
      size: 'medium'
    },
    chatBubble: {
      icon: 'MessageCircle',
      color: '#21b17a'
    },
    branding: {
      botName: '',
      tagline: 'Wir sind hier, um Ihnen zu helfen!',
      showTagline: true,
      botAvatar: '',
      userAvatar: ''
    },
    statusMessages: {
      onlineMessage: 'Wir sind online und bereit zu helfen!',
      offlineMessage: 'Wir sind momentan offline'
    },
    behavior: {
      enableTyping: true,
      typingSpeed: 'medium',
      showTypingIndicator: true
    }
  };

  // Comprehensive tab navigation matching appearance customization
  const tabs = [
    {
      id: 'widget',
      label: 'Widget',
      icon: 'Layout',
      description: 'Größe und Bubble-Einstellungen'
    },
    {
      id: 'avatars',
      label: 'Avatare',
      icon: 'User',
      description: 'Bot- und Benutzer-Avatare'
    },
    {
      id: 'typography',
      label: 'Typografie',
      icon: 'Type',
      description: 'Schriftart und -größe'
    },
    {
      id: 'colors',
      label: 'Farben',
      icon: 'Palette',
      description: 'Farbschema anpassen'
    },
    {
      id: 'content',
      label: 'Inhalte',
      icon: 'MessageSquare',
      description: 'Taglines und Status-Nachrichten'
    },
    {
      id: 'behavior',
      label: 'Verhalten',
      icon: 'Settings',
      description: 'Typing-Simulation und Animationen'
    }
  ];

  const updateAppearance = (updates) => {
    const newAppearance = {
      ...appearance,
      ...updates
    };
    onUpdate({ appearance: newAppearance });
    setHasUnsavedChanges(true);
  };

  // Individual update functions for each section
  const updateWidget = (widgetUpdates) => {
    updateAppearance({
      widget: {
        ...appearance?.widget,
        ...widgetUpdates
      }
    });
  };

  const updateChatBubble = (bubbleUpdates) => {
    updateAppearance({
      chatBubble: {
        ...appearance?.chatBubble,
        ...bubbleUpdates
      }
    });
  };

  const updateBranding = (brandingUpdates) => {
    updateAppearance({
      branding: {
        ...appearance?.branding,
        ...brandingUpdates
      }
    });
  };

  const updateTypography = (typographyUpdates) => {
    updateAppearance({
      typography: {
        ...appearance?.typography,
        ...typographyUpdates
      }
    });
  };

  const updateColorScheme = (colorSchemeUpdates) => {
    updateAppearance({
      colorScheme: {
        ...appearance?.colorScheme,
        ...colorSchemeUpdates
      }
    });
  };

  const updateStatusMessages = (statusUpdates) => {
    updateAppearance({
      statusMessages: {
        ...appearance?.statusMessages,
        ...statusUpdates
      }
    });
  };

  const updateBehavior = (behaviorUpdates) => {
    updateAppearance({
      behavior: {
        ...appearance?.behavior,
        ...behaviorUpdates
      }
    });
  };

  const handleReset = () => {
    if (window?.confirm('Möchten Sie alle Styling-Einstellungen zurücksetzen?')) {
      onUpdate({ appearance: null });
      setHasUnsavedChanges(false);
    }
  };

  // Render tab content with live preview support
  const renderTabContent = () => {
    switch (activeTab) {
      case 'widget':
        return (
          <div className="space-y-8">
            <WidgetSizeSelector
              selectedSize={appearance?.widget?.size || 'medium'}
              onSizeChange={(size) => updateWidget({ size })}
            />
            <ChatBubbleCustomizer
              bubbleIcon={appearance?.chatBubble?.icon || 'MessageCircle'}
              bubbleColor={appearance?.chatBubble?.color || '#21b17a'}
              onIconChange={(icon) => updateChatBubble({ icon })}
              onColorChange={(color) => updateChatBubble({ color })}
            />
          </div>
        );
      
      case 'avatars':
        return (
          <AvatarUploader
            botAvatar={appearance?.branding?.botAvatar || ''}
            userAvatar={appearance?.branding?.userAvatar || ''}
            onBotAvatarChange={(botAvatar) => updateBranding({ botAvatar })}
            onUserAvatarChange={(userAvatar) => updateBranding({ userAvatar })}
          />
        );
      
      case 'typography':
        return (
          <TypographyControls
            fontSize={appearance?.typography?.fontSize || '14'}
            fontFamily={appearance?.typography?.fontFamily || 'system'}
            onFontSizeChange={(fontSize) => updateTypography({ fontSize })}
            onFontFamilyChange={(fontFamily) => updateTypography({ fontFamily })}
          />
        );
      
      case 'colors':
        return (
          <ColorSchemeCustomizer
            colorScheme={appearance?.colorScheme}
            onColorSchemeChange={updateColorScheme}
          />
        );
      
      case 'content':
        return (
          <div className="space-y-8">
            <TaglineConfigurator
              showTagline={appearance?.branding?.showTagline ?? true}
              taglineText={appearance?.branding?.tagline || 'Wir sind hier, um Ihnen zu helfen!'}
              onShowTaglineChange={(showTagline) => updateBranding({ showTagline })}
              onTaglineTextChange={(tagline) => updateBranding({ tagline })}
            />
            <StatusMessagesConfig
              onlineMessage={appearance?.statusMessages?.onlineMessage || 'Wir sind online und bereit zu helfen!'}
              offlineMessage={appearance?.statusMessages?.offlineMessage || 'Wir sind momentan offline'}
              onOnlineMessageChange={(onlineMessage) => updateStatusMessages({ onlineMessage })}
              onOfflineMessageChange={(offlineMessage) => updateStatusMessages({ offlineMessage })}
            />
          </div>
        );
      
      case 'behavior':
        return (
          <TypingSimulationControls
            enableTyping={appearance?.behavior?.enableTyping ?? true}
            typingSpeed={appearance?.behavior?.typingSpeed || 'medium'}
            showTypingIndicator={appearance?.behavior?.showTypingIndicator ?? true}
            onEnableTypingChange={(enableTyping) => updateBehavior({ enableTyping })}
            onTypingSpeedChange={(typingSpeed) => updateBehavior({ typingSpeed })}
            onShowTypingIndicatorChange={(showTypingIndicator) => updateBehavior({ showTypingIndicator })}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Erscheinungsbild & Live-Vorschau</h3>
          <p className="text-sm text-muted-foreground">
            Passen Sie das Aussehen und Verhalten Ihres Chat-Widgets mit Live-Vorschau an.
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          {hasUnsavedChanges && (
            <div className="flex items-center space-x-2 text-warning">
              <Icon name="AlertCircle" size={16} />
              <span className="text-sm">Ungespeicherte Änderungen</span>
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            iconName="RotateCcw"
            iconPosition="left"
          >
            Zurücksetzen
          </Button>
        </div>
      </div>

      {/* Main Content - Split Layout like Appearance Page */}
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Configuration Panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tab Navigation */}
          <div className="border-b border-border">
            <div className="flex flex-wrap gap-1 -mb-px">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`
                    flex items-center space-x-2 px-4 py-3 border-b-2 transition-all duration-200
                    ${activeTab === tab?.id
                      ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/50'
                    }
                  `}
                >
                  <Icon name={tab?.icon} size={16} />
                  <div className="text-left">
                    <div className="text-sm font-medium">{tab?.label}</div>
                    <div className="text-xs opacity-80 hidden sm:block">{tab?.description}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-card border border-border rounded-lg p-6">
            {renderTabContent()}
          </div>
        </div>

        {/* Live Preview Panel */}
        <div className="lg:col-span-1">
          <div className="sticky top-24">
            <div className="bg-card border border-border rounded-lg p-6">
              <LivePreviewPanel
                widgetSize={appearance?.widget?.size || 'medium'}
                bubbleIcon={appearance?.chatBubble?.icon || 'MessageCircle'}
                bubbleColor={appearance?.chatBubble?.color || '#21b17a'}
                botAvatar={appearance?.branding?.botAvatar || ''}
                userAvatar={appearance?.branding?.userAvatar || ''}
                fontSize={appearance?.typography?.fontSize || '14'}
                fontFamily={appearance?.typography?.fontFamily || 'system'}
                colorScheme={appearance?.colorScheme}
                showTagline={appearance?.branding?.showTagline ?? true}
                taglineText={appearance?.branding?.tagline || 'Wir sind hier, um Ihnen zu helfen!'}
                onlineMessage={appearance?.statusMessages?.onlineMessage || 'Wir sind online und bereit zu helfen!'}
                offlineMessage={appearance?.statusMessages?.offlineMessage || 'Wir sind momentan offline'}
                enableTyping={appearance?.behavior?.enableTyping ?? true}
                typingSpeed={appearance?.behavior?.typingSpeed || 'medium'}
                showTypingIndicator={appearance?.behavior?.showTypingIndicator ?? true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8 p-4 bg-muted rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-foreground mb-1">Erweiterte Design-Optionen</h4>
            <p className="text-sm text-muted-foreground">
              Vollständige Anpassungsmöglichkeiten mit Live-Vorschau für professionelle Chat-Widgets
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window?.open('/appearance-customization', '_blank')}
              iconName="ExternalLink"
              iconPosition="left"
            >
              Vollständiger Designer
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window?.open('/chat-testing-interface', '_blank')}
              iconName="MessageSquare"
              iconPosition="left"
            >
              Chat testen
            </Button>
          </div>
        </div>
      </div>

      {/* Agent-Specific Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div>
          <p className="text-sm font-medium text-foreground">Agent Design-Konfiguration</p>
          <p className="text-xs text-muted-foreground">
            Diese Einstellungen werden im appearance-Feld des Agents gespeichert
          </p>
        </div>
        <div className="text-xs text-muted-foreground">
          Supabase Integration • Live-Synchronisation
        </div>
      </div>
    </div>
  );
};

export default StyleSettings;